// @givmo/app-admin — the company deposit's donor disclosure (CAM-X4 web, the company deposit).
// ★ MONEY-MOVING ★
//
// Adding funds is an immediate, irrevocable donation to Givmo Charitable Fund, and on the portal the
// COMPANY is its donor, so the Add funds dialog shows the approved company body before it charges —
// `ADD_FUNDS` for `payer=company` (CAM-X3; the registry row's `applies_to_state` is `COMPANY`) — and
// echoes it on the deposit (`shown_disclaimer_*`). The backend's deposit gate checks that echo against
// the same resolution before any charge (`409 DISCLOSURE_STALE` on a mismatch, nothing charged).
//
// FLOOR-LESS ON PURPOSE: the compiled-in ADD_FUNDS floor is the PERSONAL body, which a company must
// never be shown, so a failed resolve is no body at all and the dialog holds Deposit.
//
// ONLY THE COMPANY BODY (CAM-X4 P3, as iOS `DisclaimerViewModel` checks): a backend that predates
// CAM-X3 ignores `payer` and answers the personal body with a 200, so a row resolved for any other
// state counts as a failed resolve too.

import { givmoClient } from '@givmo/api-client';
import {
  createDisclosureCache,
  resolveDisclosure,
  type ResolvedDisclosure,
} from '@givmo/disclosure';

/** The registry state of the company deposit body (the backend's `FundraiserState.COMPANY`). */
export const COMPANY_DEPOSIT_STATE = 'COMPANY';

// One ETag cache for the session: reopening the dialog revalidates with a conditional GET
// (304 = headers only) instead of refetching the body.
const cache = createDisclosureCache();

/**
 * Resolve the company deposit body. `null` = nothing to show — a 404, a server error, a transport
 * failure, or a body for any state but {@link COMPANY_DEPOSIT_STATE}; the dialog treats that as
 * blocking. Never throws.
 */
export async function resolveDepositDisclosure(): Promise<ResolvedDisclosure | null> {
  const resolved = await resolveDisclosure({
    client: givmoClient,
    surface: 'ADD_FUNDS',
    payer: 'company',
    cache,
  });
  if (resolved === null || resolved.isFloor) return null;
  return resolved.appliesToState === COMPANY_DEPOSIT_STATE ? resolved : null;
}
